import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
  ViewContainerRef,
  ComponentRef
} from '@angular/core';
import { ComponentPortal, CdkPortalOutletAttachedRef, CdkPortalOutlet } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { TranslateDirective } from '@ngx-translate/core';

import { IconComponent } from '../../components/icon/icon.component';
import { TranslatePipe } from '../../pipes/translate.pipe';

import { FinePrintService, FinePrints, FinePrintContentComponent } from './fine-print.service';
import { FinePrintItemComponent, FinePrintItemType } from './fine-print-item.component';

/**
 * The Fine Print Component
 *
 * This component is responsible for creating fine print on the page
 */
@Component({
  selector: 'td-wb-fine-print',
  templateUrl: 'fine-print.component.html',
  styleUrls: ['fine-print.component.scss'],
  imports: [
    IconComponent,
    FinePrintItemComponent,
    TranslatePipe,
    CdkPortalOutlet,
    TranslateDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FinePrintComponent  implements OnInit, OnDestroy {
  /**
   * The namespace for where the locale for the fine print is located
   */
  namespace: string = '';
  /**
   * Whether the fine print is collapsed
   */
  collapsed: boolean;

  /**
   * The details of the fine print to be shown
   */
  finePrints: FinePrints;

  @HostBinding('class.td-wb-fine-print--hidden')
  get hidden(): boolean { return !this.hasFinePrints; }

  /**
   * A subscription to the [Fine Print Service's onFinePrintUpdate observable]{@link FinePrintService#onFinePrintUpdate}
   */
  private finePrintSubscription: Subscription;

  private hasFinePrints: boolean;

  /**
   * The constructor of the component
   *
   * @param changeDetectorRef
   * @param finePrintService The service that is used to fetch the details for the fine print
   * and keep track of the collapsed state
   */
  constructor(private changeDetectorRef: ChangeDetectorRef,
              private viewContainerRef: ViewContainerRef,
              private finePrintService: FinePrintService) {
  }

  /**
   * This function is called when the component is initialized and is used to listen for fine print changes from
   * [Fine Print Service's onFinePrintUpdate observable]{@link FinePrintService#onFinePrintUpdate}
   */
  ngOnInit(): void {
    this.onFinePrintChange();
    this.finePrintSubscription = this.finePrintService.onFinePrintUpdate.subscribe((): void => {
      this.onFinePrintChange();
    });
  }

  hasItems(itemType?: FinePrintItemType): boolean {
    if (this.finePrints !== undefined) {
      if (itemType !== undefined && this.finePrints[itemType + 's'] !== undefined) {
        return this.finePrints[itemType + 's'].length > 0;
      }
      return Object.keys(this.finePrints).some((key: string): boolean => this.finePrints[key].length > 0);
    }
    return false;
  }

  /**
   * This function is called when the component is destroyed and is used to unsubscribe from the subscription to
   * [Fine Print Service's onFinePrintUpdate observable]{@link FinePrintService#onFinePrintUpdate}
   */
  ngOnDestroy(): void {
    if (this.finePrintSubscription !== undefined) {
      this.finePrintSubscription.unsubscribe();
    }
  }

  /**
   * Returns the current iconId for the icon to show, based on whether or not the fine prints are collapsed
   *
   * @returns The iconId. square--plus if it is collapsed, square-minus otherwise.
   */
  getIconId(): string {
    return this.collapsed ? 'expand' : 'collapse';
  }

  /**
   * This is the function that is called when the used clicks the link to toggle the fine prints between
   * collapsed and open
   */
  onIconClick(): false {
    this.setCollapsed(this.finePrintService.invertState());
    this.setFinePrints();
    this.changeDetectorRef.markForCheck();
    return false;
  }

  isContentComponent(item: string | FinePrintContentComponent): item is FinePrintContentComponent {
    return typeof item !== 'string';
  }

  getContentComponentPortal(item: FinePrintContentComponent): ComponentPortal<unknown> {
    return new ComponentPortal(item.content, this.viewContainerRef);
  }

  setComponentInputs(portalOutletRef: CdkPortalOutletAttachedRef, component: FinePrintContentComponent): void {
    if (portalOutletRef instanceof ComponentRef) {
      if (component.contentData !== undefined) {
        Object.keys(component.contentData).forEach((key: string): void => {
          portalOutletRef.instance[key] = component.contentData[key];
        });
      }
      portalOutletRef.changeDetectorRef.detectChanges();
    }
  }

  /**
   * A private function used to set the finePrints property based on the service
   */
  private setFinePrints(): void {
    this.namespace = this.finePrintService.getNamespace();
    // Do not set finePrints from service unless namespace is defined
    if (this.namespace !== undefined) {
      this.finePrints = this.finePrintService.getFinePrints();
      Promise.resolve().then((): void => {
        this.hasFinePrints = this.hasItems();
      });
    }
  }

  /**
   * A private function that is called when the service notifies the component of a fine print change
   */
  private onFinePrintChange(): void {
    this.finePrints = {} as FinePrints;
    this.setCollapsed(this.finePrintService.getState());
    this.setFinePrints();
    this.changeDetectorRef.detectChanges();
  }

  /**
   * This function is used to set the collapsed property
   */
  private setCollapsed(collapsed: boolean): void {
    this.collapsed = collapsed;
  }
}
